<script lang="ts">
  import type { AnyTypedEntity, Person, Event, Topic, Other, HistoricalDate, User, EntityType } from '../lib/types'
  import { hasPermission } from '../lib/types'

  type EntitySortMode = 'first-mention' | 'historical-date' | 'name'
  type EntityFilters = { persons: boolean; events: boolean; topics: boolean; others: boolean }
  type EntityCounts = { persons: number; events: number; topics: number; others: number }
  type TopicDuration = { start: number; end: number; duration: number }

  interface Props {
    entities: AnyTypedEntity[]
    entityFilters: EntityFilters
    entitySortMode: EntitySortMode
    entityCounts: EntityCounts
    highlightedPersonId: number | null
    highlightedEventId: number | null
    highlightedTopicId: number | null
    currentUser: User | null
    videoDuration: number
    style?: string
    getTopicDurations: (topicId: number) => TopicDuration[]
    onToggleFilter: (filter: 'persons' | 'events' | 'topics' | 'others') => void
    onSetSortMode: (mode: EntitySortMode) => void
    onSeekToTime: (seconds: number) => void
    onEditPerson: (person: Person) => void
    onEditEvent: (event: Event) => void
    onEditTopic: (topic: Topic) => void
    onEditOther: (other: Other) => void
    onShowCrossVideos: (entityType: EntityType, entityId: number, entityName: string) => void
    onScroll: () => void
  }

  let {
    entities,
    entityFilters,
    entitySortMode,
    entityCounts,
    highlightedPersonId,
    highlightedEventId,
    highlightedTopicId,
    currentUser,
    videoDuration,
    style = '',
    getTopicDurations,
    onToggleFilter,
    onSetSortMode,
    onSeekToTime,
    onEditPerson,
    onEditEvent,
    onEditTopic,
    onEditOther,
    onShowCrossVideos,
    onScroll,
  }: Props = $props()

  let entitiesList: HTMLDivElement

  function formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  function formatHistoricalDate(date: HistoricalDate): string {
    const year = date.year < 0 ? `${Math.abs(date.year)} BC` : `${date.year}`
    if (date.day && date.month) {
      return `${date.day}-${date.month}-${year}`
    }
    if (date.month) {
      return `${date.month}/${year}`
    }
    return year
  }

  function isHighlighted(entity: AnyTypedEntity): boolean {
    return (
      (entity.entityType === 'person' && highlightedPersonId === entity.id) ||
      (entity.entityType === 'event' && highlightedEventId === entity.id) ||
      (entity.entityType === 'topic' && highlightedTopicId === entity.id)
    )
  }

  function handleEdit(entity: AnyTypedEntity) {
    if (entity.entityType === 'person') onEditPerson(entity as Person)
    else if (entity.entityType === 'event') onEditEvent(entity as Event)
    else if (entity.entityType === 'topic') onEditTopic(entity as Topic)
    else if (entity.entityType === 'other') onEditOther(entity as Other)
  }

  export function getEntitiesList(): HTMLDivElement | undefined {
    return entitiesList
  }
</script>

<section data-testid="entities-panel" class="flex flex-col bg-surface-1 border-r border-border-default overflow-hidden" {style}>
  <div class="flex items-center justify-between px-2 h-10 border-b border-border-default shrink-0">
    <div class="flex gap-1">
      <button
        class="h-6 px-2 text-xs rounded cursor-pointer transition-colors border {entityFilters.persons ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-surface-2 border-border-default text-text-muted hover:bg-surface-3'}"
        aria-pressed={entityFilters.persons}
        onclick={() => onToggleFilter('persons')}
        title="Focus on People"
      >
        People{#if entityCounts.persons > 0}<span class="ml-1 text-[10px] opacity-70">{entityCounts.persons}</span>{/if}
      </button>
      <button
        class="h-6 px-2 text-xs rounded cursor-pointer transition-colors border {entityFilters.events ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-surface-2 border-border-default text-text-muted hover:bg-surface-3'}"
        aria-pressed={entityFilters.events}
        onclick={() => onToggleFilter('events')}
        title="Focus on Events"
      >
        Events{#if entityCounts.events > 0}<span class="ml-1 text-[10px] opacity-70">{entityCounts.events}</span>{/if}
      </button>
      <button
        class="h-6 px-2 text-xs rounded cursor-pointer transition-colors border {entityFilters.topics ? 'bg-green-500/20 border-green-500/50 text-green-300' : 'bg-surface-2 border-border-default text-text-muted hover:bg-surface-3'}"
        aria-pressed={entityFilters.topics}
        onclick={() => onToggleFilter('topics')}
        title="Focus on Topics"
      >
        Topics{#if entityCounts.topics > 0}<span class="ml-1 text-[10px] opacity-70">{entityCounts.topics}</span>{/if}
      </button>
      <button
        class="h-6 px-2 text-xs rounded cursor-pointer transition-colors border {entityFilters.others ? 'bg-orange-500/20 border-orange-500/50 text-orange-300' : 'bg-surface-2 border-border-default text-text-muted hover:bg-surface-3'}"
        aria-pressed={entityFilters.others}
        onclick={() => onToggleFilter('others')}
        title="Focus on Others"
      >
        Other{#if entityCounts.others > 0}<span class="ml-1 text-[10px] opacity-70">{entityCounts.others}</span>{/if}
      </button>
    </div>
    <div class="flex rounded overflow-hidden">
      <button
        class="h-6 px-2 text-xs border-none cursor-pointer {entitySortMode === 'first-mention' ? 'bg-surface-4 text-text-primary' : 'bg-surface-2 text-text-muted hover:bg-surface-3'}"
        aria-pressed={entitySortMode === 'first-mention'}
        onclick={() => onSetSortMode('first-mention')}
        title="Sort by first mention"
      >
        1st<span class="ml-0.5 text-[10px]">▼</span>
      </button>
      <button
        class="h-6 px-2 text-xs border-none cursor-pointer {entitySortMode === 'historical-date' ? 'bg-surface-4 text-text-primary' : 'bg-surface-2 text-text-muted hover:bg-surface-3'}"
        aria-pressed={entitySortMode === 'historical-date'}
        onclick={() => onSetSortMode('historical-date')}
        title="Sort by date"
      >
        Date<span class="ml-0.5 text-[10px]">▼</span>
      </button>
      <button
        class="h-6 px-2 text-xs border-none cursor-pointer {entitySortMode === 'name' ? 'bg-surface-4 text-text-primary' : 'bg-surface-2 text-text-muted hover:bg-surface-3'}"
        aria-pressed={entitySortMode === 'name'}
        onclick={() => onSetSortMode('name')}
        title="Sort by name"
      >
        A-Z<span class="ml-0.5 text-[10px]">▼</span>
      </button>
    </div>
  </div>

  {#if entities.length > 0}
    <div class="flex-1 overflow-y-auto p-2 space-y-2" bind:this={entitiesList} onscroll={onScroll}>
      {#each entities as entity}
        {@const entityColor = entity.entityType === 'person' ? 'purple' : entity.entityType === 'event' ? 'amber' : entity.entityType === 'topic' ? 'green' : 'orange'}
        <div
          class="rounded-lg p-3 border transition-colors
            {entity.entityType === 'person' ? 'bg-purple-500/10 border-purple-500/30' : ''}
            {entity.entityType === 'event' ? 'bg-amber-500/10 border-amber-500/30' : ''}
            {entity.entityType === 'topic' ? 'bg-green-500/10 border-green-500/30' : ''}
            {entity.entityType === 'other' ? 'bg-orange-500/10 border-orange-500/30' : ''}
            {isHighlighted(entity) ? 'ring-2 ring-white/50' : ''}"
          data-entity-type={entity.entityType}
          data-entity-id={entity.id}
        >
          <div class="flex items-center gap-2 flex-wrap">
            {#if entity.entityType === 'event' && 'dateStart' in entity && entity.dateStart}
              <span class="text-xs text-amber-400/80">{formatHistoricalDate(entity.dateStart)}{'dateEnd' in entity && entity.dateEnd ? ` - ${formatHistoricalDate(entity.dateEnd)}` : ''}</span>
            {/if}
            <span class="font-medium text-sm text-text-primary">{entity.name}</span>
            <a
              class="text-xs opacity-50 hover:opacity-100 transition-opacity"
              href="https://www.google.com/search?q={encodeURIComponent(entity.name)}"
              target="_blank"
              rel="noopener noreferrer"
              title="Search Google"
            >🔍</a>
            {#if 'video_count' in entity && (entity.video_count ?? 0) > 1}
              <button
                class="text-xs opacity-50 hover:opacity-100 transition-opacity cursor-pointer bg-transparent border-none p-0"
                onclick={() => onShowCrossVideos(entity.entityType, entity.id, entity.name)}
                title="Appears in {entity.video_count} videos"
              >
                <span class="inline-flex items-center gap-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect width="18" height="18" x="3" y="3" rx="2"/><path d="m9 8 6 4-6 4Z"/>
                  </svg>
                  <span class="text-[10px]">{entity.video_count}</span>
                </span>
              </button>
            {/if}
            {#if entity.entityType === 'topic' && 'isAdvertisement' in entity && entity.isAdvertisement}
              <span class="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded">Ad</span>
            {/if}
            {#if 'internalOnly' in entity && entity.internalOnly}
              <span class="text-[10px] px-1.5 py-0.5 bg-surface-3 text-text-muted rounded">local</span>
            {/if}
            {#if hasPermission(currentUser, 'can_annotate')}
              <button
                class="ml-auto text-xs opacity-30 hover:opacity-100 transition-opacity cursor-pointer bg-transparent border-none"
                onclick={() => handleEdit(entity)}
                title="Edit"
              >✎</button>
            {/if}
          </div>

          {#if 'aliases' in entity && entity.aliases && entity.aliases.length > 0}
            <details class="mt-2 text-xs">
              <summary class="cursor-pointer text-text-muted hover:text-text-secondary">
                {entity.aliases.length} alias{entity.aliases.length === 1 ? '' : 'es'}
              </summary>
              <div class="flex flex-wrap gap-1 mt-1">
                {#each entity.aliases as alias}
                  <span class="px-1.5 py-0.5 bg-surface-3 rounded text-text-muted">{alias}</span>
                {/each}
              </div>
            </details>
          {/if}

          {#if entity.description}
            <p class="mt-2 text-xs text-text-secondary leading-relaxed">{entity.description}</p>
          {/if}

          {#if entity.entityType === 'event' && 'participants' in entity && entity.participants.length > 0}
            <div class="flex flex-wrap gap-1 mt-2">
              {#each entity.participants as participant}
                <span class="px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-300 rounded">{participant}</span>
              {/each}
            </div>
          {/if}

          {#if entity.mentions.length > 0}
            <div class="mt-2 h-2 bg-surface-2 rounded relative">
              {#if entity.entityType === 'topic'}
                {#each getTopicDurations(entity.id) as segment}
                  <button
                    class="absolute h-full rounded cursor-pointer transition-opacity hover:opacity-80
                      {entity.entityType === 'topic' ? 'bg-green-500/60' : ''}"
                    style="left: {videoDuration > 0 ? (segment.start / videoDuration) * 100 : 0}%; width: {videoDuration > 0 ? (segment.duration / videoDuration) * 100 : 0}%"
                    onclick={() => onSeekToTime(segment.start)}
                    title="{formatTime(segment.start)} - {formatTime(segment.end)}"
                  ></button>
                {/each}
              {:else}
                {#each entity.mentions as mention}
                  <button
                    class="absolute w-1.5 h-1.5 rounded-full top-1/2 -translate-y-1/2 cursor-pointer transition-opacity hover:opacity-80
                      {entity.entityType === 'person' ? 'bg-purple-400' : ''}
                      {entity.entityType === 'event' ? 'bg-amber-400' : ''}
                      {entity.entityType === 'other' ? 'bg-orange-400' : ''}"
                    style="left: {videoDuration > 0 ? (mention.timecode / videoDuration) * 100 : 0}%"
                    onclick={() => onSeekToTime(mention.timecode)}
                    title={formatTime(mention.timecode)}
                  ></button>
                {/each}
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {:else}
    <p class="flex-1 flex items-center justify-center text-sm text-text-muted text-center p-4">
      No entities found. Analyze the transcript first to extract persons, events, and topics.
    </p>
  {/if}
</section>
